import {
	Children,
	createContext,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	useContext,
	useId,
	useMemo,
	useState,
} from "react";
import { cn } from "../../lib/utils";
import { Button, Icon } from "../../primitives";
import { trackingAttrs } from "../../utils";

/**
 * How an item's caption is disclosed. `collapsible` keeps captions behind a per-item toggle so
 * a long list stays scannable when most captions are long-form prose.
 */
export type DefinitionListCaptionMode = "visible" | "collapsible";

type ListContextValue = {
	captionMode: DefinitionListCaptionMode;
	stickyHeaders: boolean;
	stickyHeaderClassName: string;
};

const ListContext = createContext<ListContextValue>({
	captionMode: "visible",
	stickyHeaders: false,
	stickyHeaderClassName: "top-0",
});

type RootProps = HTMLAttributes<HTMLDivElement> & {
	ref?: Ref<HTMLDivElement>;
	captionMode?: DefinitionListCaptionMode;
	/** Pins section titles to the top of the nearest scroll container while their items scroll past. */
	stickyHeaders?: boolean;
	/**
	 * Classes applied with sticky section titles (default `top-0`). Use a larger `top-*` when
	 * sticky chrome sits above the list in the same scroll container.
	 */
	stickyHeaderClassName?: string;
	trackingId?: string;
	testId?: string;
	children?: ReactNode;
};

function Root({
	className,
	captionMode = "visible",
	stickyHeaders = false,
	stickyHeaderClassName = "top-0",
	trackingId,
	testId = "definition-list",
	children,
	ref,
	...rest
}: RootProps) {
	const context = useMemo(
		() => ({ captionMode, stickyHeaders, stickyHeaderClassName }),
		[captionMode, stickyHeaders, stickyHeaderClassName],
	);
	return (
		<ListContext.Provider value={context}>
			<div
				ref={ref}
				data-testid={testId}
				className={cn("flex flex-col gap-5", className)}
				{...trackingAttrs(trackingId)}
				{...rest}
			>
				{children}
			</div>
		</ListContext.Provider>
	);
}
Root.displayName = "DefinitionList";

type SectionProps = HTMLAttributes<HTMLElement> & {
	ref?: Ref<HTMLElement>;
	title: ReactNode;
	trackingId?: string;
	testId?: string;
};

function Section({ title, className, trackingId, testId, children, ref, ...rest }: SectionProps) {
	const { stickyHeaders, stickyHeaderClassName } = useContext(ListContext);
	if (Children.toArray(children).length === 0) return null;
	return (
		<section
			ref={ref}
			data-testid={testId}
			className={cn("flex flex-col", className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{/* Prefer untrimmed blocks over `Text` — trim collapses stacked rows. */}
			<div
				className={cn(
					"pb-2 text-[11px] font-semibold uppercase leading-[16px] tracking-[0.6px] text-tertiary",
					stickyHeaders && cn("sticky z-10 bg-surface-base pt-1", stickyHeaderClassName),
				)}
			>
				{title}
			</div>
			<div className="flex flex-col divide-y divide-bc-default">{children}</div>
		</section>
	);
}
Section.displayName = "DefinitionList.Section";

type ItemProps = HTMLAttributes<HTMLDivElement> & {
	ref?: Ref<HTMLDivElement>;
	label: ReactNode;
	/** Secondary line under the value — explanation, source note, etc. */
	caption?: ReactNode;
	/**
	 * `stacked` puts the value under the label, for values of any length. `inline` pairs them on
	 * one row with the value trailing — only for values short enough to never wrap.
	 */
	layout?: "stacked" | "inline";
	trackingId?: string;
	testId?: string;
};

function Item({
	label,
	caption,
	layout = "stacked",
	className,
	trackingId,
	testId = "definition-list-item",
	children,
	ref,
	...rest
}: ItemProps) {
	const { captionMode } = useContext(ListContext);
	const [isCaptionOpen, setIsCaptionOpen] = useState(false);
	const captionId = useId();

	const hasCaption = caption != null && !(typeof caption === "string" && caption.trim() === "");
	const isCollapsible = hasCaption && captionMode === "collapsible";
	const isCaptionVisible = hasCaption && (captionMode === "visible" || isCaptionOpen);

	const labelText = <span className="text-[12px] leading-[16px] text-tertiary">{label}</span>;
	const labelNode = isCollapsible ? (
		<Button
			type="button"
			variant="ghost"
			aria-expanded={isCaptionOpen}
			aria-controls={captionId}
			onClick={() => setIsCaptionOpen((open) => !open)}
			className="group h-auto w-fit gap-1 px-0 py-0 text-left"
			data-testid={`${testId}-caption-toggle`}
		>
			{labelText}
			<Icon
				type={isCaptionOpen ? "chevron-up" : "chevron-down"}
				size="micro"
				color="subtle"
				className="transition-colors group-hover:text-tertiary"
				aria-hidden
			/>
		</Button>
	) : (
		labelText
	);
	const valueNode = (
		<div
			className={cn(
				"break-words text-[14px] font-medium leading-[20px] text-primary",
				layout === "stacked" ? "whitespace-pre-wrap" : "ml-auto pl-4 text-right",
			)}
		>
			{children}
		</div>
	);

	return (
		<div
			ref={ref}
			data-testid={testId}
			className={cn("flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0", className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{layout === "inline" ? (
				<div className="flex items-baseline justify-between">
					{labelNode}
					{valueNode}
				</div>
			) : (
				<>
					{labelNode}
					{valueNode}
				</>
			)}
			{isCaptionVisible && (
				// Indented behind a rule so long-form prose reads as evidence for the value above it
				// rather than as the next item's label.
				<div
					id={captionId}
					className="mt-1 border-l border-bc-strong pl-2.5 text-[12px] leading-[18px] text-secondary"
				>
					{caption}
				</div>
			)}
		</div>
	);
}
Item.displayName = "DefinitionList.Item";

export const DefinitionList = Object.assign(Root, { Section, Item });

export type DefinitionListProps = RootProps;
export type DefinitionListSectionProps = SectionProps;
export type DefinitionListItemProps = ItemProps;
